/**
 * Error Suppression Utility
 * Suppresses all script errors, runtime errors, and React errors from displaying on screen and console
 */

export const suppressAllErrors = () => {
 
  window.addEventListener('error', (event) => {
    // Silently catch error - don't log to console, don't show on screen
    // Prevent default error display
    event.preventDefault();
    return true;
  }, true);

  // Handle unhandled promise rejections silently
  window.addEventListener('unhandledrejection', (event) => {
    // Silently catch rejection - don't log to console, don't show on screen
    // Prevent default error display
    event.preventDefault();
    return true;
  });

  // Disable React error overlay and suppress all error displays
  // This prevents errors from showing on screen AND in console
  if (typeof window !== 'undefined') {
    // Override window.onerror to catch all errors silently
    window.onerror = function(message, source, lineno, colno, error) {
      // Silently catch error - don't log to console, don't show on screen
      // Prevent error from showing on screen
      return true;
    };
    
    // Function to hide ALL error overlays (works in both dev and production)
    const hideErrorOverlay = () => {
      try {
        // Try multiple selectors for React error overlay
        const selectors = [
          '#react-error-overlay',
          '.react-error-overlay',
          '[data-react-error-overlay]',
          'iframe[src*="react-error-overlay"]',
          // Catch any element with error overlay classes or IDs
          '[id*="error-overlay"]',
          '[id*="errorOverlay"]',
          '[class*="error-overlay"]',
          '[class*="errorOverlay"]',
          // Catch webpack error overlay
          '#webpack-dev-server-client-overlay',
          '.webpack-dev-server-client-overlay',
          'div[style*="z-index"]'
        ];
        
        selectors.forEach(selector => {
          try {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
              if (element) {
                const text = element.textContent || '';
                const id = element.id || '';
                const className = element.className || '';
                const style = element.getAttribute('style') || '';
                
                // Check if this looks like an error overlay
                if (text.includes('Uncaught runtime errors') || 
                    text.includes('Script error') ||
                    text.includes('ERROR') ||
                    id.includes('error') ||
                    className.includes('error') ||
                    style.includes('z-index') && (text.includes('error') || id.includes('overlay'))) {
                  element.style.display = 'none';
                  element.style.visibility = 'hidden';
                  element.style.opacity = '0';
                  element.style.pointerEvents = 'none';
                  element.style.zIndex = '-9999';
                  element.remove();
                }
              }
            });
          } catch (e) {
            // Ignore selector errors
          }
        });
        
        // Remove any element that contains error text (more aggressive check)
        const allDivs = document.querySelectorAll('div');
        allDivs.forEach(element => {
          const text = (element.textContent || '').trim();
          const id = (element.id || '').toLowerCase();
          const className = (element.className || '').toLowerCase();
          const style = element.getAttribute('style') || '';
          
          // Check for error overlay indicators
          if ((text.includes('Uncaught runtime errors') || 
               text.includes('Script error') ||
               text.includes('ERROR') && text.length < 200) ||
              id.includes('error-overlay') ||
              id.includes('erroroverlay') ||
              className.includes('error-overlay') ||
              className.includes('erroroverlay') ||
              (style.includes('z-index') && (text.includes('error') || id.includes('overlay')))) {
            element.style.display = 'none';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.style.pointerEvents = 'none';
            element.style.zIndex = '-9999';
            element.remove();
          }
        });
        
        // Also try to remove any error overlay iframes
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          if (iframe.src && (iframe.src.includes('react-error-overlay') || iframe.src.includes('error-overlay'))) {
            iframe.style.display = 'none';
            iframe.remove();
          }
        });
      } catch (e) {
        // Ignore errors in overlay removal
      }
    };
    
    // Hide overlay immediately
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hideErrorOverlay);
    } else {
      hideErrorOverlay();
    }
    
    // Hide overlay repeatedly to catch dynamically injected overlays
    setInterval(hideErrorOverlay, 100);
    
    // Use MutationObserver to catch dynamically injected overlays
    const observer = new MutationObserver(() => {
      hideErrorOverlay();
    });
    
    // Start observing as soon as body exists
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'id', 'style']
      });
    } else {
      // Wait for body if it doesn't exist yet
      const bodyObserver = new MutationObserver(() => {
        if (document.body) {
          observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'id', 'style']
          });
          bodyObserver.disconnect();
        }
      });
      bodyObserver.observe(document.documentElement, {
        childList: true
      });
    }
    
    // Also hide on any error event
    window.addEventListener('error', hideErrorOverlay, true);
    window.addEventListener('unhandledrejection', hideErrorOverlay, true);
  }

  console.warn = () => {};
  console.log = () => {}; 
  console.error = () => {};
};

